import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, EyeOff, ArrowLeft, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import Logo from "../components/Logo";

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    gender: '',
    profile_image: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    preferred_currency: 'USD',
    role: 'school_student',
    grade: '9',
    education: '',
    area_of_interest: '',
    working_area: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  // Fetch countries from database
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${API}/countries`);
        setCountries(response.data.countries || []);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
        // Fallback to common countries if API fails
        setCountries([
          { name: 'India', code: 'IN', currency_code: 'INR' },
          { name: 'United States', code: 'US', currency_code: 'USD' },
          { name: 'United Kingdom', code: 'GB', currency_code: 'GBP' },
          { name: 'Canada', code: 'CA', currency_code: 'CAD' },
          { name: 'Australia', code: 'AU', currency_code: 'AUD' },
          { name: 'Germany', code: 'DE', currency_code: 'EUR' },
          { name: 'France', code: 'FR', currency_code: 'EUR' },
          { name: 'Japan', code: 'JP', currency_code: 'JPY' },
          { name: 'China', code: 'CN', currency_code: 'CNY' }
        ]);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      
      setProfileImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
        setFormData({ ...formData, profile_image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfileImage = () => {
    setProfileImageFile(null);
    setProfileImagePreview(null);
    setFormData({ ...formData, profile_image: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        gender: formData.gender,
        profile_image: formData.profile_image,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        zip_code: formData.zip_code,
        preferred_currency: formData.preferred_currency,
        role: formData.role,
      };

      // Add conditional fields based on role
      if (formData.role === 'school_student') {
        registerData.grade = parseInt(formData.grade);
      } else if (formData.role === 'college_student') {
        registerData.education = formData.education;
      } else if (formData.role === 'jobseeker') {
        registerData.education = formData.education;
        registerData.area_of_interest = formData.area_of_interest;
      } else if (formData.role === 'professional') {
        registerData.education = formData.education;
        registerData.working_area = formData.working_area;
      }

      const response = await axios.post(`${API}/auth/register`, registerData);
      
      // Check if verification is required
      if (response.data.requires_verification) {
        toast.success('✅ Registration successful! Please check your email to verify your account.', {
          duration: 6000
        });
        // Redirect to login page with verification message
        navigate('/login', { 
          state: { 
            message: 'Thank you for registering! Please check your email to verify your account before logging in.',
            email: response.data.email
          } 
        });
      } else {
        // Old flow (if verification is disabled)
        const { token, user } = response.data;
        login(token, user);
        toast.success('Registration successful! Welcome to SkillEdge!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4 py-8">
      <div className="w-full max-w-3xl">
        {/* Back to Home Button */}
        <Button
          variant="ghost"
          className="mb-4 text-blue-700 hover:bg-blue-100 hover:text-blue-900 font-medium"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Home
        </Button>

        <Card className="p-6 md:p-10 shadow-xl border-2 border-blue-100 bg-white">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <Logo className="h-12" linkTo="/" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-blue-600 mb-3">
              Join Us
            </h1>
            <p className="text-base md:text-lg text-gray-600">Start your career discovery journey today</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-blue-700 border-b-2 border-blue-200 pb-2">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Full Name</Label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Email Address</Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="+91-1234567890"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Gender</Label>
                  <Select value={formData.gender} onValueChange={(val) => setFormData({ ...formData, gender: val })}>
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select your gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Profile Photo Upload */}
              <div>
                <Label className="text-sm font-medium text-gray-700">Profile Photo (Optional)</Label>
                <div className="mt-2 flex items-center gap-4">
                  {profileImagePreview ? (
                    <div className="relative">
                      <img
                        src={profileImagePreview}
                        alt="Profile preview"
                        className="w-20 h-20 rounded-full object-cover border-2 border-blue-300"
                      />
                      <button
                        type="button"
                        onClick={removeProfileImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-300">
                      <Upload className="text-blue-500" size={24} />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      id="profile-image"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="profile-image">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        onClick={() => document.getElementById('profile-image').click()}
                      >
                        <Upload className="mr-2" size={16} />
                        Choose Photo
                      </Button>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Max size: 5MB. Formats: JPG, PNG, GIF</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Password Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-blue-700 border-b-2 border-blue-200 pb-2">Security</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 6 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="h-11 pr-12 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 p-2"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Confirm Password</Label>
                  <Input
                    type="password"
                    placeholder="Re-enter password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Address Details Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-blue-700 border-b-2 border-blue-200 pb-2">Address Details</h3>
              
              <div>
                <Label className="text-sm font-medium text-gray-700">Address</Label>
                <Input
                  type="text"
                  placeholder="Street address, building, apartment"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">City</Label>
                  <Input
                    type="text"
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">State</Label>
                  <Input
                    type="text"
                    placeholder="State"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Country</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(val) => {
                      // Find the selected country to get its currency
                      const selectedCountry = countries.find(c => c.name === val);
                      setFormData({ 
                        ...formData, 
                        country: val, 
                        preferred_currency: selectedCountry?.currency_code || formData.preferred_currency 
                      });
                    }}
                  >
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder={loadingCountries ? "Loading countries..." : "Select country"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {countries.map((country) => (
                        <SelectItem key={country.code || country.name} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Postal Code</Label>
                  <Input
                    type="text"
                    placeholder="ZIP/Postal code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700">Preferred Currency</Label>
                <Select 
                  value={formData.preferred_currency} 
                  onValueChange={(val) => setFormData({ ...formData, preferred_currency: val })}
                >
                  <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {/* Get unique currencies from countries */}
                    {[...new Set(countries.map(c => c.currency_code))].filter(Boolean).sort().map((currency) => (
                      <SelectItem key={currency} value={currency}>
                        {currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Auto-selected based on country, but you can change it</p>
              </div>
            </div>

            {/* Professional Details Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-blue-700 border-b-2 border-blue-200 pb-2">Professional Details</h3>
              
              <div>
                <Label className="text-sm font-medium text-gray-700">I am a</Label>
                <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                  <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school_student">School Student</SelectItem>
                    <SelectItem value="college_student">College Student</SelectItem>
                    <SelectItem value="jobseeker">Jobseeker</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Conditional Education/Grade Fields */}
              {formData.role === 'school_student' && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Grade/Class</Label>
                  <Select value={formData.grade} onValueChange={(val) => setFormData({ ...formData, grade: val })}>
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['9', '10', '11', '12'].map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          Grade {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.role === 'college_student' && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Education/Program</Label>
                  <Select value={formData.education} onValueChange={(val) => setFormData({ ...formData, education: val })}>
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select your program" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BSc">BSc</SelectItem>
                      <SelectItem value="BA">BA</SelectItem>
                      <SelectItem value="BCom">BCom</SelectItem>
                      <SelectItem value="BCA">BCA</SelectItem>
                      <SelectItem value="MCA">MCA</SelectItem>
                      <SelectItem value="BBA">BBA</SelectItem>
                      <SelectItem value="MBA">MBA</SelectItem>
                      <SelectItem value="CA">CA</SelectItem>
                      <SelectItem value="CS">CS</SelectItem>
                      <SelectItem value="Mechanical Engineering">Mechanical Engineering</SelectItem>
                      <SelectItem value="Civil Engineering">Civil Engineering</SelectItem>
                      <SelectItem value="Computer Engineering">Computer Engineering</SelectItem>
                      <SelectItem value="Electrical Engineering">Electrical Engineering</SelectItem>
                      <SelectItem value="Electronics Engineering">Electronics Engineering</SelectItem>
                      <SelectItem value="PhD">PhD</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(formData.role === 'jobseeker' || formData.role === 'professional') && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Education</Label>
                  <Select value={formData.education} onValueChange={(val) => setFormData({ ...formData, education: val })}>
                    <SelectTrigger className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select your education" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10th Pass">10th Pass</SelectItem>
                      <SelectItem value="12th Pass">12th Pass</SelectItem>
                      <SelectItem value="Diploma">Diploma</SelectItem>
                      <SelectItem value="BSc">BSc</SelectItem>
                      <SelectItem value="BA">BA</SelectItem>
                      <SelectItem value="BCom">BCom</SelectItem>
                      <SelectItem value="BCA">BCA</SelectItem>
                      <SelectItem value="MCA">MCA</SelectItem>
                      <SelectItem value="BBA">BBA</SelectItem>
                      <SelectItem value="MBA">MBA</SelectItem>
                      <SelectItem value="B.Tech/BE">B.Tech/BE</SelectItem>
                      <SelectItem value="M.Tech/ME">M.Tech/ME</SelectItem>
                      <SelectItem value="CA">CA</SelectItem>
                      <SelectItem value="CS">CS</SelectItem>
                      <SelectItem value="PhD">PhD</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Additional Fields for Jobseeker */}
              {formData.role === 'jobseeker' && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Area of Interest</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Marketing, IT, Finance"
                    value={formData.area_of_interest}
                    onChange={(e) => setFormData({ ...formData, area_of_interest: e.target.value })}
                    required
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Additional Fields for Professional */}
              {formData.role === 'professional' && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Working Area</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Software Development, Teaching"
                    value={formData.working_area}
                    onChange={(e) => setFormData({ ...formData, working_area: e.target.value })}
                    required
                    className="h-11 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-start gap-2 text-sm pt-2">
              <input type="checkbox" required className="mt-1" />
              <span className="text-gray-600">
                I agree to the{' '}
                <Link to="/terms" className="text-blue-600 hover:underline font-medium">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-blue-600 hover:underline font-medium">
                  Privacy Policy
                </Link>
              </span>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md hover:shadow-lg transition-all"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            {/* Google Sign Up Button - Placeholder for Phase 5 */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-2 border-gray-300 hover:bg-gray-50 font-medium text-gray-700"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                window.location.href = `${window.location.origin}/api/auth/google/login`;
              }}
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 font-semibold hover:underline">
                Login here
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
